import React, { useContext } from "react";
import { Trash, Plus } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import GraphConfigFormContext from "@/store/contexts/GraphConfigFormContext";

interface Threshold {
  y: number;
  label: string;
  color: string;
  dash: boolean;
  axis: "left" | "right";
}

interface ThresholdSelectorProps {}

const ThresholdSelector = ({}: ThresholdSelectorProps) => {
  const { config, setConfig } = useContext(GraphConfigFormContext);
  
  if (!config) return null;
  
  const thresholds = config.graph_options.thresholds || [];
  
  const onChange = (newThresholds: Threshold[]) => {
    setConfig({
      ...config,
      graph_options: {
        ...config.graph_options,
        thresholds: newThresholds,
      },
    });
  };
  // Add a new threshold
  const handleAddThreshold = () => {
    const newThreshold: Threshold = {
      y: 0,
      label: "",
      color: "#FF0000",
      dash: false,
      axis: "left",
    };
    onChange([...thresholds, newThreshold]);
  };

  // Remove a threshold
  const handleRemoveThreshold = (index: number) => {
    const newThresholds = [...thresholds];
    newThresholds.splice(index, 1);
    onChange(newThresholds);
  };

  // Update a threshold
  const handleUpdateThreshold = (
    index: number,
    field: keyof Threshold,
    value: any
  ) => {
    const newThresholds = [...thresholds];
    newThresholds[index] = {
      ...newThresholds[index],
      [field]: value,
    };
    onChange(newThresholds);
  };

  return (
    <div className="flex flex-col gap-4">
      <Label>Threshold Lines</Label>

      {thresholds.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No thresholds defined. Add one below.
        </p>
      )}

      {thresholds.map((threshold, index) => (
        <div key={index} className="flex flex-col gap-3 p-4 border rounded-md">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Threshold {index + 1}</Label>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleRemoveThreshold(index)}
            >
              <Trash className="h-4 w-4 text-destructive" />
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Y Value</Label>
              <Input
                type="number"
                value={threshold.y}
                onChange={(e) =>
                  handleUpdateThreshold(
                    index,
                    "y",
                    parseFloat(e.target.value) || 0
                  )
                }
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-xs">Label</Label>
              <Input
                type="text"
                value={threshold.label}
                onChange={(e) =>
                  handleUpdateThreshold(index, "label", e.target.value)
                }
                className="mt-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Color</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="color"
                  value={threshold.color}
                  onChange={(e) =>
                    handleUpdateThreshold(index, "color", e.target.value)
                  }
                  className="w-10 h-10 p-1"
                />
                <Input
                  type="text"
                  value={threshold.color}
                  onChange={(e) =>
                    handleUpdateThreshold(index, "color", e.target.value)
                  }
                  className="flex-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs pb-1">Axis</Label>
              <Select
                value={threshold.axis}
                onValueChange={(value: "left" | "right") =>
                  handleUpdateThreshold(index, "axis", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select axis" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="left">Left</SelectItem>
                    <SelectItem value="right">Right</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-xs">Dashed Line</Label>
            <Switch
              checked={threshold.dash}
              onCheckedChange={(checked) =>
                handleUpdateThreshold(index, "dash", checked)
              }
            />
          </div>
        </div>
      ))}

      <Button
        variant="outline"
        className="w-full mt-2"
        onClick={handleAddThreshold}
      >
        <Plus className="h-4 w-4 mr-2" /> Add Threshold
      </Button>
    </div>
  );
};

export default ThresholdSelector;
